import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import SchoolDetailsModal from '../../components/SchoolDetailsModal';
import Header from '../../../../components/header/Header';
import Footer from '../../../../components/footer/Footer';
import Breadcrumb from '../../../../shared/components/Breadcrumb';
import {
  ArrowLeft,
  BookOpen,
  Library,
  Wifi,
  Database,
  TrendingUp,
  Award,
  XCircle,
  BookMarked,
  Globe,
} from 'lucide-react';
import {
  ComparisonBarChart,
  DistributionPieChart,
  StatCard,
} from '../../components/analytics';

// Lookup maps (re-exported from shared constants)
import * as LOOKUP from '../../constants/lookupMaps';
import {
  getLibrarySummary,
  getDigitalLibrarySummary,
  getAutomatedSystemsLibrary,
  getAvgBooksPerSchool,
  getBestTopTenLibraries,
  getLibrariesNeedingUpgrade,
  LibrarySummaryResponse,
  DigitalLibraryResponse,
  AutomatedSystemsResponse,
  AvgBooksResponse,
  BestLibrary,
  LibraryUpgrade
} from '../../services/api';

/**
 * Unwrap one settled request. A single failing endpoint must not discard the
 * results that did arrive, so each is read independently and a rejection just
 * leaves that one dataset null.
 */
const settledValue = <T,>(result: PromiseSettledResult<T>, label: string): T | null => {
  if (result.status === 'fulfilled') return result.value;
  console.error(`Error fetching ${label}:`, result.reason);
  return null;
};

export default function LibraryProfileAnalysis() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'books' | 'automation' | 'internet' | 'facilities'>('books');
  const [selectedSchool, setSelectedSchool] = useState<{
    schoolID: number;
    schoolName: string;
    infraData?: any;
    tabLabel?: string;
  } | null>(null);
  const [loading, setLoading] = useState(true);

  const [summaryData, setSummaryData] = useState<LibrarySummaryResponse | null>(null);
  const [digitalData, setDigitalData] = useState<DigitalLibraryResponse | null>(null);
  const [automationData, setAutomationData] = useState<AutomatedSystemsResponse | null>(null);
  const [avgBooksData, setAvgBooksData] = useState<AvgBooksResponse | null>(null);
  const [bestLibraries, setBestLibraries] = useState<BestLibrary[]>([]);
  const [upgradeLibraries, setUpgradeLibraries] = useState<LibraryUpgrade[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [summary, digital, automation, avgBooks, best, upgrade] = await Promise.allSettled([
          getLibrarySummary(),
          getDigitalLibrarySummary(),
          getAutomatedSystemsLibrary(),
          getAvgBooksPerSchool(),
          getBestTopTenLibraries(),
          getLibrariesNeedingUpgrade()
        ]);

        setSummaryData(settledValue(summary, 'library summary'));
        setDigitalData(settledValue(digital, 'digital library summary'));
        setAutomationData(settledValue(automation, 'library automation'));
        setAvgBooksData(settledValue(avgBooks, 'average books per school'));
        const bestValue = settledValue(best, 'best libraries');
        if (bestValue) setBestLibraries(bestValue);
        const upgradeValue = settledValue(upgrade, 'libraries needing upgrade');
        if (upgradeValue) setUpgradeLibraries(upgradeValue);
      } catch (error) {
        console.error('Error fetching library data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const tabs = [
    { id: 'books', label: 'Books Collection', icon: BookOpen },
    { id: 'automation', label: 'Automation', icon: Database },
    { id: 'internet', label: 'Internet & Digital', icon: Wifi },
    { id: 'facilities', label: 'Facilities', icon: Library },
  ];

  const addPercentage = (items: { category: string; pmshriCount: number; nonPmshriCount: number; total: number }[]) =>
    items.map(item => ({
      ...item,
      pmshriPercentage: item.total > 0 ? (item.pmshriCount / item.total) * 100 : 0
    }));

  const getComparisonData = () => {
    if (!summaryData || !digitalData || !automationData) return [];

    switch (activeTab) {
      case 'books':
        // Using Total Books and Digital Books as categories since specific book types aren't in the new API
        return addPercentage([
          {
            category: 'Total Books',
            pmshriCount: summaryData.PMShree.totalBooks,
            nonPmshriCount: summaryData.NonPMShree.totalBooks,
            total: summaryData.Total.totalBooks
          },
          {
            category: 'Digital Books',
            pmshriCount: digitalData.PMShree.digitalBooks,
            nonPmshriCount: digitalData.NonPMShree.digitalBooks,
            total: digitalData.Total.digitalBooks
          },
        ]);
      case 'automation':
        // Using Software Breakdown keys
        const softwares = Object.keys(automationData.SoftwareBreakdown.Total);
        return addPercentage(softwares.map(key => ({
          category: LOOKUP.LIBRARY_AUTOMATION_SOFTWARE_NAME[key as keyof typeof LOOKUP.LIBRARY_AUTOMATION_SOFTWARE_NAME] || `Software ${key}`,
          pmshriCount: automationData.SoftwareBreakdown.PMShree[key] || 0,
          nonPmshriCount: automationData.SoftwareBreakdown.NonPMShree[key] || 0,
          total: automationData.SoftwareBreakdown.Total[key] || 0
        })));
      case 'internet':
        return addPercentage([
          {
            category: 'With Internet',
            pmshriCount: digitalData.PMShree.withInternet,
            nonPmshriCount: digitalData.NonPMShree.withInternet,
            total: digitalData.Total.withInternet
          },
          // "High Speed", "With WiFi" etc. are not available in new API, using automated count as proxy or just showing available data
          {
            category: 'Automated Libraries',
            pmshriCount: digitalData.PMShree.automatedCount,
            nonPmshriCount: digitalData.NonPMShree.automatedCount,
            total: digitalData.Total.automatedCount
          },
        ]);
      case 'facilities':
        return addPercentage([
          {
            category: 'AC Room',
            pmshriCount: summaryData.PMShree.withAC,
            nonPmshriCount: summaryData.NonPMShree.withAC,
            total: summaryData.Total.withAC
          },
          {
            category: 'With Projector',
            pmshriCount: summaryData.PMShree.withProjector,
            nonPmshriCount: summaryData.NonPMShree.withProjector,
            total: summaryData.Total.withProjector
          },
        ]);
      default:
        return [];
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f5f5f7]">
        <div className="text-xl text-purple-700 font-semibold">Loading Library Analytics...</div>
      </div>
    );
  }

  // A dataset the page cannot render without is missing. Say so — this used to
  // fall back into the loader above, which read as a hung page rather than a
  // failed request.
  if (!summaryData || !digitalData || !automationData || !avgBooksData) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#f5f5f7] px-4 text-center">
        <div className="text-xl text-red-600 font-semibold">Library analytics could not be loaded.</div>
        <div className="text-sm text-gray-500 mt-2">Some data is unavailable right now. Please try again later.</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-[#f5f5f7]">
      <Header />
      <div className="w-full max-w-7xl mx-auto px-4 pt-4">
        <Breadcrumb />
      </div>

      <main className="flex-grow w-full max-w-7xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-[#6A1786] mb-2 flex items-center gap-2">
              <Library className="w-7 h-7" />
              Library Profile Analysis
            </h1>
            <p className="text-gray-600">
              Books collection, automation systems, internet connectivity, and library facilities
            </p>
          </div>
          <button
            onClick={() => navigate('/kvs-samagam/analytics')}
            className="mt-2 md:mt-0 flex items-center gap-2 px-4 py-2 border border-purple-700 text-purple-700 rounded-lg hover:bg-purple-50 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Analytics
          </button>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
          <StatCard
            title="Total Libraries"
            value={(summaryData?.Total?.libraryCount ?? 0).toLocaleString()}
            icon={<Library className="w-6 h-6" />}
            color="purple"
            subtitle="Across all KVs"
          />
          <StatCard
            title="Total Books"
            value={(summaryData.Total.totalBooks / 1000000).toFixed(1) + 'M'}
            icon={<BookOpen className="w-6 h-6" />}
            color="blue"
            subtitle="Physical Books"
          />
          <StatCard
            title="Digital Books"
            value={(digitalData.Total.digitalBooks / 1000).toFixed(1) + 'K'}
            icon={<Globe className="w-6 h-6" />}
            color="green"
            subtitle="E-Books Available"
          />
          <StatCard
            title="Automated Systems"
            value={(automationData?.Summary?.Total?.automatedCount ?? 0).toLocaleString()}
            icon={<Database className="w-6 h-6" />}
            color="orange"
            subtitle={`${(summaryData?.Total?.libraryCount > 0 ? ((automationData?.Summary?.Total?.automatedCount ?? 0) / (summaryData?.Total?.libraryCount ?? 1) * 100) : 0).toFixed(1)}% of libraries`}
          />
          <StatCard
            title="Avg. Books/School"
            value={avgBooksData.Total.averageBooks.toFixed(0)}
            icon={<BookMarked className="w-6 h-6" />}
            color="teal"
            subtitle="Per library"
          />
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-xl border border-gray-200 mb-6">
          <nav className="flex overflow-x-auto">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as any)}
                  className={`flex items-center gap-2 px-6 py-4 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${activeTab === tab.id
                      ? 'text-purple-700 border-purple-600 bg-purple-50'
                      : 'text-gray-600 border-transparent hover:text-gray-900 hover:bg-gray-50'
                    }`}
                >
                  <Icon className="w-4 h-4" />
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <ComparisonBarChart
            data={getComparisonData()}
            title={`${tabs.find(t => t.id === activeTab)?.label} - PM-SHRI vs Non PM-SHRI`}
            xAxisLabel="Category"
          />
          <DistributionPieChart
            data={getComparisonData().map(d => ({ name: d.category, value: d.total }))}
            title={`${tabs.find(t => t.id === activeTab)?.label} Distribution`}
          />
        </div>

        {/* Detailed Stats Table */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">
            {tabs.find(t => t.id === activeTab)?.label} Details
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Category</th>
                  <th className="text-center py-3 px-4 text-sm font-semibold text-green-600">PM-SHRI</th>
                  <th className="text-center py-3 px-4 text-sm font-semibold text-blue-600">Non PM-SHRI</th>
                  <th className="text-center py-3 px-4 text-sm font-semibold text-purple-600">Total</th>
                  <th className="text-center py-3 px-4 text-sm font-semibold text-gray-600">PM-SHRI %</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {getComparisonData().map((row) => (
                  <tr key={row.category} className="hover:bg-gray-50">
                    <td className="py-3 px-4 text-sm text-gray-700 font-medium">{row.category}</td>
                    <td className="py-3 px-4 text-sm text-center text-green-600 font-medium">{(row.pmshriCount ?? 0).toLocaleString()}</td>
                    <td className="py-3 px-4 text-sm text-center text-blue-600 font-medium">{(row.nonPmshriCount ?? 0).toLocaleString()}</td>
                    <td className="py-3 px-4 text-sm text-center text-purple-600 font-bold">{(row.total ?? 0).toLocaleString()}</td>
                    <td className="py-3 px-4 text-sm text-center">
                      <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                        {(row.total > 0 ? ((row.pmshriCount ?? 0) / row.total) * 100 : 0).toFixed(1)}%
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Schools Lists */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="px-6 py-4 bg-gradient-to-r from-purple-50 to-blue-50 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                <Award className="w-5 h-5 text-purple-600" />
                Best Libraries
              </h3>
            </div>
            <div className="divide-y divide-gray-100 max-h-96 overflow-y-auto">
              {bestLibraries.map((school, index) => (
                <div key={school.schoolID} className="px-6 py-4 hover:bg-gray-50 cursor-pointer" onClick={() => school.schoolID && setSelectedSchool({ schoolID: school.schoolID, schoolName: school.schoolName, infraData: school, tabLabel: 'Library' })}>
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${index === 0 ? 'bg-yellow-100 text-yellow-700' :
                          index === 1 ? 'bg-gray-100 text-gray-600' :
                            index === 2 ? 'bg-orange-100 text-orange-700' :
                              'bg-gray-50 text-gray-500'
                        }`}>
                        {index + 1}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h4 className="font-medium text-gray-800">{school.schoolName}</h4>
                          {school.isPMShree && (
                            <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full">PM-SHRI</span>
                          )}
                        </div>
                        <p className="text-sm text-gray-500">{school.stateName} • {school.roName}</p>
                        <div className="flex flex-wrap gap-2 mt-2">
                          <span className="text-xs bg-blue-50 text-blue-600 px-2 py-1 rounded">{(school.totalPhysicalBooks ?? 0).toLocaleString()} Books</span>
                          <span className="text-xs bg-purple-50 text-purple-600 px-2 py-1 rounded">Score: {school.score}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-xl border border-red-200 overflow-hidden">
            <div className="px-6 py-4 bg-gradient-to-r from-red-50 to-orange-50 border-b border-red-200">
              <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-red-600" />
                Libraries Needing Upgrade
              </h3>
            </div>
            <div className="divide-y divide-gray-100 max-h-96 overflow-y-auto">
              {upgradeLibraries.map((school) => {
                const issues = [];
                if (!school.needsUpgradeDetail.automation) issues.push('No Automation');
                if (!school.needsUpgradeDetail.internet) issues.push('No Internet');
                if (!school.needsUpgradeDetail.ac) issues.push('No AC');
                if (!school.needsUpgradeDetail.projector) issues.push('No Projector');

                return (
                  <div key={school.schoolID} className="px-6 py-4 hover:bg-red-50 cursor-pointer" onClick={() => school.schoolID && setSelectedSchool({ schoolID: school.schoolID, schoolName: school.schoolName, infraData: school, tabLabel: 'Library' })}>
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-2">
                          <h4 className="font-medium text-gray-800">{school.schoolName}</h4>
                          <span className="px-2 py-0.5 text-xs rounded-full bg-red-100 text-red-700">
                            NEEDS UPGRADE
                          </span>
                        </div>
                        <p className="text-sm text-gray-500">{school.stateName} • {school.roName}</p>
                        <div className="text-sm text-red-600 mt-1 flex flex-wrap gap-1">
                          {issues.slice(0, 3).map((issue, i) => (
                            <span key={i} className="flex items-center gap-1 bg-red-50 px-2 py-0.5 rounded text-xs">
                              <XCircle className="w-3 h-3" /> {issue}
                            </span>
                          ))}
                          {issues.length > 3 && <span className="text-xs text-gray-500">+{issues.length - 3} more</span>}
                        </div>
                      </div>
                      <button
                        onClick={e => { e.stopPropagation(); school.schoolID && setSelectedSchool({ schoolID: school.schoolID, schoolName: school.schoolName, infraData: school, tabLabel: 'Library' }); }}
                        className="px-3 py-1 text-xs bg-purple-600 text-white rounded-lg hover:bg-purple-700">
                        View
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Detailed Analysis Reports Section */}
        <div className="mt-8 bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-[#6A1786] mb-4">Detailed Analysis Reports</h3>
          <p className="text-sm text-gray-600 mb-4">Access comprehensive analysis reports for each category</p>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {[
              { name: 'Infrastructure', path: '/kvs-samagam/analytics/infrastructure', icon: '🏫', color: 'blue' },
              { name: 'Enrollment', path: '/kvs-samagam/analytics/enrollment', icon: '👨‍🎓', color: 'green' },
              { name: 'ICT Profile', path: '/kvs-samagam/analytics/ict-profile', icon: '💻', color: 'purple' },
              { name: 'Library Profile', path: '/kvs-samagam/analytics/library-profile', icon: '📚', color: 'orange' },
              { name: 'CWSN Profile', path: '/kvs-samagam/analytics/cwsn-profile', icon: '♿', color: 'pink' },
              { name: 'NCC Profile', path: '/kvs-samagam/analytics/ncc-profile', icon: '🎖️', color: 'cyan' },
              { name: 'Sports', path: '/kvs-samagam/analytics/sports', icon: '🏆', color: 'yellow' },
              { name: 'Green Building', path: '/kvs-samagam/analytics/green-building', icon: '🌿', color: 'teal' },
              { name: 'Safety', path: '/kvs-samagam/analytics/safety', icon: '🛡️', color: 'red' },
              { name: 'Special Labs', path: '/kvs-samagam/analytics/special-labs', icon: '🔬', color: 'indigo' },
              { name: 'Water Profile', path: '/kvs-samagam/analytics/miscellaneous', icon: '💧', color: 'teal' },
            ].map((item) => {
              const isCurrentPage = item.name === 'Library Profile';
              return (
                <button
                  key={item.name}
                  onClick={() => !isCurrentPage && navigate(item.path)}
                  disabled={isCurrentPage}
                  className={`bg-gradient-to-br from-${item.color}-50 to-${item.color}-100 rounded-lg p-4 border border-${item.color}-200 text-center transition-all ${
                    isCurrentPage 
                      ? 'opacity-50 cursor-not-allowed' 
                      : 'hover:shadow-lg cursor-pointer hover:scale-105'
                  }`}
                >
                  <span className="text-3xl">{item.icon}</span>
                  <p className="text-sm font-medium text-gray-700 mt-2">{item.name}</p>
                </button>
              );
            })}
          </div>
        </div>
      </main>

      <Footer />

      {selectedSchool && (
        <SchoolDetailsModal
          schoolID={selectedSchool.schoolID}
          onClose={() => setSelectedSchool(null)}
          schoolKPIData={selectedSchool.infraData}
          selectedCategory={selectedSchool.tabLabel}
        />
      )}
    </div>
  );
}
